// Handler globali per Node: `uncaughtException` e `unhandledRejection`.
//
// Si usa un listener ATTIVO (non `uncaughtExceptionMonitor`): il monitor non
// tiene vivo l'event loop, quindi il POST asincrono della telemetria morirebbe
// quasi sempre prima di partire. Registrare un listener però DISATTIVA il
// crash di default di Node → lo si ripristina a mano: dopo capture + flush
// (con timeout), se i nostri sono gli unici listener, stampa l'errore su
// stderr ed esce con codice 1 (pattern sentry-node). Se l'utente ha propri
// handler, la decisione di uscire resta a lui. Stessa policy per
// `unhandledRejection` (da Node ≥15 il default è crashare).

import type { Client } from '../../client'

/// Sottoinsieme di `process` usato dagli handler (iniettabile nei test).
export interface ProcessLike {
  on(event: string, listener: (...args: never[]) => unknown): unknown
  removeListener(event: string, listener: (...args: never[]) => unknown): unknown
  listeners(event: string): unknown[]
  exit(code: number): void
  stderr?: { write(chunk: string): unknown }
}

export interface ProcessHandlersOptions {
  /// Tempo massimo (ms) concesso al flush prima di uscire comunque.
  flushTimeoutMs?: number
}

/// Installa gli handler. Ritorna la funzione di disinstallazione.
export function installProcessHandlers(
  client: Client,
  proc: ProcessLike = process as unknown as ProcessLike,
  options: ProcessHandlersOptions = {},
): () => void {
  const flushTimeoutMs = options.flushTimeoutMs ?? 2000

  const handle = async (event: string, error: unknown, self: unknown) => {
    client.captureException(error, { handled: false, level: 'fatal' })
    await flushWithTimeout(client, flushTimeoutMs)
    // Solo il nostro listener registrato? Allora ripristiniamo il default.
    const others = proc.listeners(event).filter((l) => l !== self)
    if (others.length === 0) {
      proc.stderr?.write(`${describeError(error)}\n`)
      proc.exit(1)
    }
  }

  const onUncaught = (error: unknown): Promise<void> =>
    handle('uncaughtException', error, onUncaught)
  const onRejection = (reason: unknown): Promise<void> =>
    handle('unhandledRejection', reason, onRejection)

  proc.on('uncaughtException', onUncaught as never)
  proc.on('unhandledRejection', onRejection as never)

  return () => {
    proc.removeListener('uncaughtException', onUncaught as never)
    proc.removeListener('unhandledRejection', onRejection as never)
  }
}

async function flushWithTimeout(client: Client, timeoutMs: number): Promise<void> {
  await Promise.race([
    client.flush(),
    new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, timeoutMs)
      ;(timer as { unref?: () => void }).unref?.()
    }),
  ])
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.stack ?? `${error.name}: ${error.message}`
  return String(error)
}
